import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { CategoryAvailability, Restriction, ServiceAvailabilityResult } from '@mews-distributor/data-access';
import { values } from '@mews/fp';
import {
    DateService,
    DateTimeService,
    DateTimeUtc,
    DateTimeUtcService,
    DateTimeUtils,
    Time,
    TimeService,
} from '@mews/framework';
import { API_MAXIMUM_INTERVAL_TIME_UNITS } from '../../constants';
import { toUtcWithoutMiliseconds } from '../../utils/dates';
import { AvailableStartTimes, ServiceAvailabilityData, TimeUnitsCategoryAvailability } from './types';
import { MakeUtcDatesParams } from './utils';

interface GetHourlyDateIntervals {
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
}

// We will only ever fetch the visible range due to API limitations.
export const getHourlyDateIntervals = ({ startUtc, endUtc }: GetHourlyDateIntervals) => {
    const hoursStartToEnd = DateTimeUtcService.diffHours(DateTimeUtcService.endOf(endUtc, 'month'), startUtc);
    return pipe(
        array.makeBy(hoursStartToEnd, (i) => DateTimeUtcService.addHours(startUtc, i)),
        array.chunksOf(API_MAXIMUM_INTERVAL_TIME_UNITS),
        array.map((chunk) => ({ start: chunk[0], end: chunk[chunk.length - 1] }))
    );
};

export const getCombinedAvailability = (availability: CategoryAvailability[][]) =>
    pipe(
        availability,
        array.flatten,
        array.reduce({}, (acc: Record<string, number[]>, { categoryId, availabilities }) => {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!acc[categoryId]) acc[categoryId] = availabilities;
            else acc[categoryId] = [...acc[categoryId], ...availabilities];
            return acc;
        }),
        record.toEntries,
        array.map(([categoryId, availabilities]) => ({ categoryId, availabilities }))
    );

export const getCombinedRestrictions = (restrictions: Restriction[][]) =>
    pipe(restrictions, array.flatten, array.uniq({ equals: (a, b) => a.id === b.id }));

export const getCombinedResponse = (response: ServiceAvailabilityResult[]) => {
    const combinedTimeUnits = pipe(
        response,
        array.map(({ timeUnitStartsUtc }) => timeUnitStartsUtc),
        array.flatten
    );

    const combinedAvailabilities = pipe(
        response,
        array.map(({ categoryAvailabilities }) => categoryAvailabilities),
        getCombinedAvailability
    );

    const combinedRestrictions = pipe(
        response.map(({ applicableRestrictions }) => applicableRestrictions),
        getCombinedRestrictions
    );

    const combinedCategoryMappings = pipe(
        response.map(({ categoryMappings }) => option.fromNullable(categoryMappings)),
        array.compact,
        array.flatten,
        array.uniq({ equals: (a, b) => a.resourceCategoryId === b.resourceCategoryId })
    );

    return {
        timeUnitStartsUtc: combinedTimeUnits,
        categoryAvailabilities: combinedAvailabilities,
        applicableRestrictions: combinedRestrictions,
        categoryMappings: combinedCategoryMappings,
    };
};

interface HourlyTransformProps {
    availabilities: number[];
    timeUnitStartsUtc: string[];
    timezone: string;
}

// aggregates hourly availabilities per day to be used more easily in the calendar
export const transformHourlyServiceCategoryAvailabilities = ({
    availabilities,
    timeUnitStartsUtc,
    timezone,
}: HourlyTransformProps): TimeUnitsCategoryAvailability =>
    pipe(
        availabilities,
        array.reduceWithIndex<number, Record<string, number>>({}, (index, acc, current) => {
            const startTimesDate = timeUnitStartsUtc[index];
            const dateWithoutTime = DateTimeUtils.dateTimeUtcStringToWallClockDate(startTimesDate, timezone).value;
            const utcDate = toUtcWithoutMiliseconds(dateWithoutTime, timezone);

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!acc[utcDate]) acc[utcDate] = 0;
            if (utcDate != null) acc[utcDate] += current;

            return acc;
        })
    );

// TODO: remove after new logic is implemented
// available start times will be stored in redux separately as it is not needed for the calendar
export const getAvailableStartTimes = ({
    availabilities,
    timeUnitStartsUtc,
    timezone,
}: HourlyTransformProps): AvailableStartTimes =>
    pipe(
        availabilities,
        array.reduceWithIndex<number, AvailableStartTimes>({}, (index, acc, availabile) => {
            const startTimesDate = timeUnitStartsUtc[index];
            const dateWithoutTime = DateTimeUtils.dateTimeUtcStringToWallClockDate(startTimesDate, timezone).value;
            const utcDate = toUtcWithoutMiliseconds(dateWithoutTime, timezone);

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!acc[utcDate]) acc[utcDate] = {};

            const time = DateTimeUtils.dateTimeUtcStringToTime(startTimesDate, timezone).value;
            acc[utcDate] = { ...acc[utcDate], [time]: availabile };

            return acc;
        })
    );

const getEarliestTime = (times: string[]) =>
    times.reduce((earliest, current) => TimeService.min(TimeService.parse(earliest), TimeService.parse(current)).value);

interface GetEarliestTimeUtc {
    dates: AvailableStartTimes;
    startDate: string;
    timezone: string;
    startTime: Time;
}

export const getEarliestTimeUtc = ({ dates, startDate, startTime, timezone }: GetEarliestTimeUtc) => {
    const startUtc = toUtcWithoutMiliseconds(DateService.parse(startDate, timezone).value, timezone);

    let times = pipe(
        dates[startUtc],
        record.toArray,
        array.filter(([_time, count]) => count > 0),
        array.map(([time]) => time)
    );

    if (startTime != null) {
        times = times.filter((time) => TimeService.isGreaterOrEqual(TimeService.parse(time), startTime));
    }

    const earliestTime = times.length > 0 ? TimeService.parse(getEarliestTime(times)) : null;

    return earliestTime != null
        ? DateTimeUtils.createDateTimeUtc({
              date: DateTimeUtils.dateTimeUtcStringToWallClockDate(startUtc, timezone),
              time: earliestTime ?? startTime,
              timezone,
          })
        : null;
};

export const getEarliestDateTime = (dateTimes?: DateTimeUtc[]) =>
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    (dateTimes?.length || 0) > 0
        ? dateTimes?.reduce((earliest, current) => DateTimeUtcService.min(earliest, current))
        : null;

interface GetEarliestDateTime {
    data: ServiceAvailabilityData;
    timezone: string;
    startUtc: DateTimeUtc;
}
// TODO: remove after new logic is implemented
export const getFirstAvailableStartTime = ({ data, timezone, startUtc }: GetEarliestDateTime) => {
    if (timezone == null) {
        return null;
    }

    const startDate = DateTimeUtils.dateTimeUtcStringToWallClockDate(startUtc.value, timezone).value;
    return pipe(
        values(data),
        array.map(({ availableStartTimes }) => availableStartTimes),
        array.reduce([], (acc: DateTimeUtc[], dates) => {
            const startTime = DateTimeUtils.dateTimeUtcStringToTime(startUtc.value, timezone);
            const earliest = dates != null ? getEarliestTimeUtc({ dates, startDate, timezone, startTime }) : null;
            return earliest != null ? [...acc, earliest] : acc;
        }),
        getEarliestDateTime // get earliest from all categories
    );
};

interface GetFirstAvailableDateTime {
    data: ServiceAvailabilityData;
    timezone: string;
    startUtc: DateTimeUtc;
}

export const getFirstAvailableDateTime = ({ data, timezone, startUtc }: GetFirstAvailableDateTime) => {
    if (timezone == null) {
        return null;
    }
    return pipe(
        values(data),
        array.map(({ timeUnitsAvailability }) =>
            pipe(
                record.toArray(timeUnitsAvailability),
                array.findFirst(
                    ([dateTimeUtc, count]) =>
                        count > 0 &&
                        DateTimeUtcService.isGreaterOrEqual(DateTimeUtcService.parse(dateTimeUtc), startUtc)
                ),
                option.map(([dateTimeUtc]) => DateTimeUtcService.parse(dateTimeUtc))
            )
        ),
        array.compact,
        getEarliestDateTime
    );
};

/**
 * @returns array of dateTimeUtc ISO strings without milliseconds by hour
 */
export const makeHourlyUtcDates = ({
    startUtc,
    endUtc,
    includeEndUtc = false,
    timezone,
}: MakeUtcDatesParams): string[] => {
    const startDateTime = DateTimeService.fromUtc(startUtc, timezone);
    const endDateTime = DateTimeService.fromUtc(endUtc, timezone);
    const createDate = (hoursFromStart: number): string =>
        pipe(
            DateTimeService.addHours(startDateTime, hoursFromStart),
            (date) => DateTimeService.toUtc(date),
            (dateTimeUtc) => DateTimeUtcService.toISOString(dateTimeUtc, { suppressMilliseconds: true })
        );
    /**
     * When using this function to find missing availability date ranges, it is important to include the last day.
     * In instances where we are using it to adjust availability, we do not want to include the last day.
     */
    const range = DateTimeService.diffHours(endDateTime, startDateTime) + (includeEndUtc ? 1 : 0);

    return array.makeBy(range, createDate);
};
